import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Users, Loader2, Mail, Phone, Building2, Trash2, Upload, Download, Search, X, RotateCcw, RefreshCw, Bot, Zap, Edit, Clock, MessageSquare, Filter, CheckCircle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import AdvancedDataGrid from '../../components/AdvancedDataGrid';
import CSVImportWizard from '../../components/CSVImportWizard';
import { formatPhone } from '../../utils/formatPhone';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700' },
];

const LEAD_TYPES = [
  { value: 'staffing_company', label: 'Staffing Company', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'client', label: 'Client', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'investor', label: 'Investor', color: 'bg-amber-100 text-amber-700' },
  { value: 'vendor', label: 'Vendor', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'franchise', label: 'Franchise', color: 'bg-purple-100 text-purple-700' },
  { value: 'misc', label: 'Misc', color: 'bg-slate-100 text-slate-700' },
];

const PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];

const initialFormData = {
  prefix: '', first_name: '', last_name: '', gender: '', title: '', company_id: '',
  location_id: '', work_email: '', personal_email: '', work_direct_phone: '',
  mobile_phone: '', linkedin_url: '', source: '', status: 'new', description: '',
  dnc: false, dne: false, envelope_mailed: false, lead_types: ['staffing_company'], lead_type: 'crm',
};

export default function LeadsPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false); // Select all across all pages
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideDNC, setHideDNC] = useState(false);
  const [selectedLeadTypes, setSelectedLeadTypes] = useState([]); // Multi-select filter for lead types
  const [selectedLists, setSelectedLists] = useState([]); // Multi-select filter for lists
  const [lists, setLists] = useState([]); // All available lists
  const [leadCategoryTab, setLeadCategoryTab] = useState(() => {
    // Load from localStorage or default to 'jobtalk'
    return localStorage.getItem('leadCategoryTab') || 'jobtalk';
  });
  const [leadTypeDropdownOpen, setLeadTypeDropdownOpen] = useState(false);
  const [listsDropdownOpen, setListsDropdownOpen] = useState(false);
  const [columnPrefs, setColumnPrefs] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  
  // AI Call states
  const [aiCallDialogOpen, setAiCallDialogOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [aiCallLoading, setAiCallLoading] = useState(false);
  
  // Sequence Enrollment states
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [sequenceEnrollLoading, setSequenceEnrollLoading] = useState(false);
  
  // Bulk update state
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [bulkUpdateField, setBulkUpdateField] = useState('');
  const [bulkUpdateValue, setBulkUpdateValue] = useState('');
  const [bulkUpdateValues, setBulkUpdateValues] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Hover activities cache
  const [leadActivitiesCache, setLeadActivitiesCache] = useState({});
  
  // Filter visibility state
  const [showFilters, setShowFilters] = useState(false);
  
  // Company autocomplete state
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  
  // Permission checks
  // Allow all users to view, edit, and delete leads
  const canWriteLeads = true;
  const canDeleteLeads = true;
  const [selectedCompanyLocations, setSelectedCompanyLocations] = useState([]);
  
  // Cache reference
  const cacheRef = useRef({
    leads: null,
    companies: null,
    users: null,
    timestamp: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  // Prevent double fetch
  const fetchedRef = useRef(false);

  // Single fetch on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData(false);
  }, []);
  
  // Close company suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCompanySuggestions && !e.target.closest('.company-autocomplete')) {
        setShowCompanySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCompanySuggestions]);
  
  const loadData = async (forceRefresh = false, tabOverride = null) => {
    const now = Date.now();
    const cache = cacheRef.current;
    
    // Use tabOverride if provided, otherwise use state
    const activeTab = tabOverride !== null ? tabOverride : leadCategoryTab;
    
    // Skip cache when tab has changed or forcing refresh
    const skipCache = forceRefresh || cache.lastTab !== activeTab;
    
    // Use cached data if valid and not forcing refresh and same tab
    if (!skipCache && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION && cache.leads) {
      setLeads(cache.leads);
      setTotal(cache.leads.length);
      setCompanies(cache.companies || []);
      setUsers(cache.users || []);
      setLoading(false);
      setPrefsLoaded(true);
      return;
    }
    
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Build query params with lead_types filter
      const params = new URLSearchParams({ limit: '1000' });
      
      // Add lead_types filter based on selected tab
      console.log('[LeadsPage] Loading data for tab:', activeTab);
      
      if (activeTab === 'franchise') {
        params.append('lead_types', 'franchise'); // Only franchise
        console.log('[LeadsPage] Applying franchise filter');
      } else if (activeTab === 'jobtalk') {
        params.append('lead_types_exclude', 'franchise'); // All except franchise
        console.log('[LeadsPage] Applying jobtalk filter (exclude franchise)');
      } else {
        console.log('[LeadsPage] Showing all leads (no filter)');
      }
      // 'all' tab sends no filter - shows everything
      
      const url = `${API}/leads?${params.toString()}`;
      console.log('[LeadsPage] Fetching from:', url);
      
      // Fetch all data sequentially to avoid rate limits
      const leadsRes = await axios.get(url);
      const leadsData = leadsRes?.data?.items || [];
      const totalCount = leadsRes?.data?.total || 0;
      console.log('[LeadsPage] Received leads count:', leadsData.length, 'Total:', totalCount);
      
      setLeads(leadsData);
      setTotal(totalCount);
      
      const companiesRes = await axios.get(`${API}/companies?limit=1000`);
      const companiesData = companiesRes?.data?.items || [];
      // Sort companies alphabetically by name
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
      
      const usersRes = await axios.get(`${API}/users`);
      const usersData = usersRes?.data || [];
      setUsers(usersData);
      
      // Fetch lists
      const listsRes = await axios.get(`${API}/franchise/lists`);
      const listsData = listsRes?.data || [];
      setLists(listsData);
      
      // Update cache with current tab
      cacheRef.current = {
        leads: leadsData,
        companies: companiesData,
        users: usersData,
        timestamp: Date.now(),
        lastTab: activeTab  // Track which tab this cache is for
      };
      
      // Load column preferences
      try {
        const prefsRes = await axios.get(`${API}/user/preferences/leads_columns`);
        if (prefsRes.data.value) setColumnPrefs(prefsRes.data.value);
      } catch (e) { /* ignore */ }
      
      if (forceRefresh) {
        toast.success('Leads data refreshed');
      }
    } catch (error) {
      console.error('[LeadsPage] Error loading data:', error);
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and refresh.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication error. Please log in again.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to load leads data';
        toast.error(errorMsg);
      }
      // Set empty data on error to prevent null reference errors
      setLeads([]);
      setCompanies([]);
      setUsers([]);
      setLists([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setPrefsLoaded(true);
    }
  };

  // Handle lead category tab change
  const handleLeadCategoryChange = (newTab) => {
    console.log('[LeadsPage] Tab changed to:', newTab);
    setLeadCategoryTab(newTab);
    // Save to localStorage
    localStorage.setItem('leadCategoryTab', newTab);
    // Invalidate cache to force fresh data
    cacheRef.current.timestamp = 0;
    // Reload data with new filter - PASS newTab directly to avoid stale state
    loadData(true, newTab);
  };

  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery('');
    setHideDNC(false);
    setSelectedIds(new Set());
    setSelectedLeadTypes([]);
    setSelectedLists([]);
    setLeadTypeDropdownOpen(false);
    setListsDropdownOpen(false);
    toast.success('Filters cleared');
  };
  
  // Force refresh from database
  const handleRefresh = () => {
    loadData(true);
  };

  // Refresh data after mutations
  const refreshLeads = async () => {
    try {
      // Build query params with lead_types filter
      const params = new URLSearchParams({ limit: '1000' });
      
      console.log('[LeadsPage] Refreshing leads for tab:', leadCategoryTab);
      
      // Add lead_types filter based on selected tab
      if (leadCategoryTab === 'franchise') {
        params.append('lead_types', 'franchise');
      } else if (leadCategoryTab === 'jobtalk') {
        params.append('lead_types_exclude', 'franchise');
      }
      // 'all' tab sends no filter
      
      const res = await axios.get(`${API}/leads?${params.toString()}`);
      const leadsData = res.data.items || [];
      console.log('[LeadsPage] Refreshed - received count:', leadsData.length);
      
      setLeads(leadsData);
      setTotal(res.data.total || 0);
      // Update cache
      cacheRef.current.leads = leadsData;
      cacheRef.current.timestamp = Date.now();
      cacheRef.current.lastTab = leadCategoryTab;
    } catch (error) {
      console.error('Refresh error:', error);
    }
  };

  const saveColumnPreferences = async (prefs) => {
    try {
      await axios.put(`${API}/user/preferences/leads_columns`, { value: prefs });
    } catch (error) {
      console.error('Failed to save column preferences');
    }
  };

  const handleColumnChange = (visibleColumns, columnOrder, columnWidths = {}) => {
    const prefs = { visibleColumns, columnOrder, columnWidths };
    setColumnPrefs(prefs);
    saveColumnPreferences(prefs);
  };

  const handleCompanyChange = (companyId) => {
    setFormData({ ...formData, company_id: companyId, location_id: '' });
    const company = companies.find(c => c.id === companyId);
    setSelectedCompanyLocations(company?.locations || []);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedCompanyLocations([]);
  };

  const handleCreate = async () => {
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === 'none') payload[k] = null;
      });
      
      // Ensure lead_types has at least Staffing Company if empty
      if (!payload.lead_types || payload.lead_types.length === 0) {
        payload.lead_types = ['staffing_company'];
      }
      
      await axios.post(`${API}/leads`, payload);
      toast.success('Lead created');
      setCreateDialogOpen(false);
      resetForm();
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLead) return;
    try {
      await axios.delete(`${API}/leads/${selectedLead.id}`);
      toast.success('Lead deleted');
      setDeleteDialogOpen(false);
      setSelectedLead(null);
      refreshLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleBulkDelete = async () => {
    let leadsToDelete;
    
    // If select all pages is active, fetch all lead IDs
    if (selectAllPages) {
      try {
        const params = new URLSearchParams({
          limit: total.toString(),
          skip: '0'
        });
        if (searchQuery) params.append('search', searchQuery);
        if (selectedLeadTypes.length > 0) {
          selectedLeadTypes.forEach(type => params.append('lead_types', type));
        }
        
        const response = await axios.get(`${API}/leads?${params.toString()}`);
        leadsToDelete = response.data.items.map(lead => lead.id);
        
        if (!window.confirm(`⚠️ WARNING: This will DELETE ALL ${leadsToDelete.length} leads across all pages. This action cannot be undone. Continue?`)) {
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch all leads');
        return;
      }
    } else {
      leadsToDelete = Array.from(selectedIds);
    }

    try {
      await Promise.all(leadsToDelete.map(id => axios.delete(`${API}/leads/${id}`)));
      toast.success(`${leadsToDelete.length} leads deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      setSelectAllPages(false);
      refreshLeads();
    } catch (error) {
      toast.error('Failed to delete some leads');
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkUpdateField) {
      toast.error('Please select a field to update');
      return;
    }

    let selectedLeads;
    
    // If select all pages is active, fetch all lead IDs
    if (selectAllPages) {
      try {
        // Fetch all lead IDs with current filters
        const params = new URLSearchParams({
          limit: total.toString(),
          skip: '0'
        });
        if (searchQuery) params.append('search', searchQuery);
        if (selectedLeadTypes.length > 0) {
          selectedLeadTypes.forEach(type => params.append('lead_types', type));
        }
        
        const response = await axios.get(`${API}/leads?${params.toString()}`);
        selectedLeads = response.data.items.map(lead => lead.id);
        
        if (!window.confirm(`This will update ALL ${selectedLeads.length} leads across all pages. Continue?`)) {
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch all leads');
        return;
      }
    } else {
      selectedLeads = Array.from(selectedIds);
      if (selectedLeads.length === 0) {
        toast.error('No leads selected');
        return;
      }
    }

    // Validate based on field type
    if (bulkUpdateField === 'lead_types' && bulkUpdateValues.length === 0) {
      toast.error('Please select at least one lead type');
      return;
    }
    if (bulkUpdateField === 'add_to_lists' && bulkUpdateValues.length === 0) {
      toast.error('Please select at least one list');
      return;
    }
    if (!['lead_types', 'add_to_lists'].includes(bulkUpdateField) && !bulkUpdateValue && bulkUpdateValue !== 'false') {
      toast.error('Please enter a value');
      return;
    }

    setBulkUpdating(true);
    try {
      let updateData = {};
      
      if (bulkUpdateField === 'lead_types') {
        updateData.lead_types = bulkUpdateValues;
      } else if (bulkUpdateField === 'add_to_lists') {
        // Add to lists - we'll need to do this individually
        const promises = selectedLeads.map(async (leadId) => {
          for (const listId of bulkUpdateValues) {
            await axios.post(`${API}/leads/${leadId}/add-to-list/${listId}`);
          }
        });
        await Promise.all(promises);
      } else if (['dnc', 'dne', 'envelope_mailed', 'bad_number'].includes(bulkUpdateField)) {
        // Boolean fields
        updateData[bulkUpdateField] = bulkUpdateValue === 'true';
      } else {
        // Text and other fields
        updateData[bulkUpdateField] = bulkUpdateValue;
      }

      // Update each selected lead (except for lists which we handled above)
      if (bulkUpdateField !== 'add_to_lists') {
        const promises = selectedLeads.map(leadId =>
          axios.put(`${API}/leads/${leadId}`, updateData)
        );
        await Promise.all(promises);
      }
      
      toast.success(`${selectedLeads.length} leads updated successfully`);
      setBulkUpdateDialogOpen(false);
      setBulkUpdateField('');
      setBulkUpdateValue('');
      setBulkUpdateValues([]);
      setSelectedIds(new Set());
      setSelectAllPages(false);
      refreshLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leads');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Fetch activities for hover card
  const fetchLeadActivities = async (leadId) => {
    // Check cache first
    if (leadActivitiesCache[leadId]) {
      return leadActivitiesCache[leadId];
    }

    try {
      const response = await axios.get(`${API}/communications/lead/${leadId}`);
      const activities = response.data.slice(0, 5);
      
      // Cache the results
      setLeadActivitiesCache(prev => ({
        ...prev,
        [leadId]: activities
      }));
      
      return activities;
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      // Cache empty array to prevent repeated failed requests
      setLeadActivitiesCache(prev => ({
        ...prev,
        [leadId]: []
      }));
      return [];
    }
  };

  // AI Call functions
  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/ai-calls/campaigns`);
      setCampaigns(response.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const openAICallDialog = () => {
    fetchCampaigns();
    setAiCallDialogOpen(true);
  };

  const handleBulkAICall = async () => {
    if (!selectedCampaignId) {
      toast.error('Please select a campaign');
      return;
    }
    
    const leadsWithPhone = Array.from(selectedIds).filter(id => {
      const lead = leads.find(l => l.id === id);
      return lead && (lead.work_direct_phone || lead.mobile_phone || lead.work_company_phone);
    });
    
    if (leadsWithPhone.length === 0) {
      toast.error('None of the selected leads have phone numbers');
      return;
    }
    
    setAiCallLoading(true);
    try {
      const response = await axios.post(`${API}/ai-calls/bulk-call`, {
        lead_ids: leadsWithPhone,
        campaign_id: selectedCampaignId
      });
      
      const { successful, total } = response.data;
      toast.success(`AI calls initiated for ${successful}/${total} leads`);
      setAiCallDialogOpen(false);
      setSelectedIds(new Set());
      setSelectedCampaignId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate AI calls');
    } finally {
      setAiCallLoading(false);
    }
  };

  // Sequence Enrollment functions
  const fetchSequences = async () => {
    try {
      const response = await axios.get(`${API}/sequences?status=active`);
      setSequences(response.data || []);
    } catch (error) {
      console.error('Failed to load sequences:', error);
    }
  };

  const openSequenceDialog = () => {
    fetchSequences();
    setSequenceDialogOpen(true);
  };

  const handleEnrollInSequence = async () => {
    if (!selectedSequenceId) {
      toast.error('Please select a sequence');
      return;
    }
    
    setSequenceEnrollLoading(true);
    try {
      const response = await axios.post(`${API}/sequences/${selectedSequenceId}/enroll`, {
        lead_ids: Array.from(selectedIds)
      });
      
      const { enrolled, skipped, total } = response.data;
      if (enrolled > 0) {
        toast.success(`${enrolled} leads enrolled in sequence`);
      }
      if (skipped > 0) {
        toast.warning(`${skipped} leads skipped (already enrolled or ineligible)`);
      }
      
      setSequenceDialogOpen(false);
      setSelectedIds(new Set());
      setSelectedSequenceId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll leads');
    } finally {
      setSequenceEnrollLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'First Name', 'Last Name', 'Prefix', 'Title', 'Company', 'Location',
      'Work Email', 'Personal Email', 'Work Direct Phone', 'Work Company Phone', 'Mobile Phone',
      'LinkedIn URL', 'X/Twitter URL', 'Source', 'Timezone', 'Status', 'Lead Types',
      'DNC', 'DNE', 'Bad Number', 'Envelope Mailed', 'Batch Name', 'Notes', 'Created At'
    ];
    const rows = filteredLeads.map(l => [
      l.first_name || '',
      l.last_name || '',
      l.prefix || '',
      l.title || '',
      l.company_name || '',
      l.location_name || '',
      l.work_email || '',
      l.personal_email || '',
      l.work_direct_phone || '',
      l.work_company_phone || '',
      l.mobile_phone || '',
      l.linkedin_url || '',
      l.x_url || '',
      l.source || '',
      l.timezone || '',
      l.status || '',
      (l.lead_types || []).join('; '),
      l.dnc ? 'Yes' : 'No',
      l.dne ? 'Yes' : 'No',
      l.bad_number ? 'Yes' : 'No',
      l.envelope_mailed ? 'Yes' : 'No',
      l.batch_name || '',
      l.description || '',
      l.created_at ? format(new Date(l.created_at), 'yyyy-MM-dd HH:mm') : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter leads
  const filteredLeads = useMemo(() => {
    let result = leads;
    if (hideDNC) result = result.filter(l => !l.dnc);
    
    // Filter by selected lead types
    if (selectedLeadTypes.length > 0) {
      result = result.filter(l => {
        const leadTypes = l.lead_types || [];
        return selectedLeadTypes.some(type => leadTypes.includes(type));
      });
    }
    
    // Filter by selected lists
    if (selectedLists.length > 0) {
      result = result.filter(l => {
        const leadListIds = l.list_ids || [];
        return selectedLists.some(listId => leadListIds.includes(listId));
      });
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().replace(/[\s\-\(\)\.]/g, ''); // Remove spaces, dashes, parens for phone search
      result = result.filter(l => {
        // Text search
        if ((l.first_name || '').toLowerCase().includes(q) ||
            (l.last_name || '').toLowerCase().includes(q) ||
            (l.work_email || '').toLowerCase().includes(q) ||
            (l.company_name || '').toLowerCase().includes(q) ||
            (l.title || '').toLowerCase().includes(q)) {
          return true;
        }
        // Phone number search - strip non-digits for matching
        const mobileClean = (l.mobile_phone || '').replace(/\D/g, '');
        const workClean = (l.work_direct_phone || '').replace(/\D/g, '');
        const qDigits = q.replace(/\D/g, '');
        if (qDigits && (mobileClean.includes(qDigits) || workClean.includes(qDigits))) {
          return true;
        }
        return false;
      });
    }
    return result;
  }, [leads, hideDNC, searchQuery, selectedLeadTypes, selectedLists]);

  // Stats
  const stats = useMemo(() => {
    const byStatus = {};
    STATUSES.forEach(s => byStatus[s.value] = 0);
    leads.forEach(l => { if (byStatus[l.status] !== undefined) byStatus[l.status]++; });
    return byStatus;
  }, [leads]);

  // Grid columns
  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filterable: true,
      render: (row) => (
        <button
          onClick={() => navigate(`/admin/leads/${row.id}`)}
          className="text-left font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
          data-testid={`lead-link-${row.id}`}
        >
          {row.first_name} {row.last_name}
          {row.dnc && <Badge variant="outline" className="ml-2 text-red-600 text-xs">DNC</Badge>}
        </button>
      ),
      getValue: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim().toLowerCase(),
    },
    {
      key: 'company_name',
      label: 'Company',
      sortable: true,
      filterable: true,
      filterOptions: companies.map(c => ({ value: c.name, label: c.name })),
      render: (row) => row.company_name || '—',
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      filterable: true,
      render: (row) => row.title || '—',
    },
    {
      key: 'work_email',
      label: 'Email',
      sortable: true,
      filterable: true,
      getValue: (row) => (row.work_email || '').toLowerCase(),
      render: (row) => row.work_email ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/leads/${row.id}`, { state: { openEmail: true } });
          }}
          className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 text-sm"
        >
          <Mail className="h-3 w-3" />{row.work_email}
        </button>
      ) : '—',
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: false,
      filterable: true,
      getValue: (row) => {
        // Combine both phone numbers and remove non-digits for search
        const mobile = row.mobile_phone || '';
        const work = row.work_direct_phone || '';
        // Return both numbers concatenated (digits only) for flexible search
        return `${mobile} ${work}`.toLowerCase();
      },
      render: (row) => {
        const phone = row.mobile_phone || row.work_direct_phone;
        return phone ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/leads/${row.id}`, { state: { openCall: true } });
            }}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
          >
            <Phone className="h-3 w-3" />{formatPhone(phone)}
          </button>
        ) : '—';
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: STATUSES.map(s => ({ value: s.value, label: s.label })),
      render: (row) => {
        const s = STATUSES.find(st => st.value === row.status) || STATUSES[0];
        return <Badge className={s.color}>{s.label}</Badge>;
      },
    },
    {
      key: 'owner_name',
      label: 'Owner',
      sortable: true,
      filterable: true,
      filterOptions: users.map(u => ({ value: u.full_name, label: u.full_name })),
      render: (row) => row.owner_name || '—',
    },
    {
      key: 'source',
      label: 'Source',
      sortable: true,
      filterable: true,
      render: (row) => row.source || '—',
    },
    {
      key: 'envelope_mailed',
      label: 'Envelope Mailed',
      sortable: true,
      filterable: true,
      filterType: 'boolean',
      filterOptions: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      render: (row) => (
        <Badge variant="outline" className={row.envelope_mailed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'}>
          {row.envelope_mailed ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row) => row.created_at ? format(parseISO(row.created_at), 'MMM d, yyyy') : '—',
      getValue: (row) => row.created_at || '',
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: (row) => canDeleteLeads ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={(e) => { e.stopPropagation(); setSelectedLead(row); setDeleteDialogOpen(true); }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null,
    },
  ], [navigate, companies, users, canDeleteLeads]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM Leads</h1>
          <p className="text-slate-500">{total} total leads</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? "Hide Filters" : "Filter"}
          </Button>
          <Button variant="outline" onClick={() => setCsvImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />Import
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-lead-btn">
            <Plus className="h-4 w-4 mr-2" />Add Lead
          </Button>
        </div>
      </div>

      {/* Lead Category Tabs with Lead Type Toggles */}
      <Tabs value={leadCategoryTab} onValueChange={handleLeadCategoryChange}>
        <div className="flex items-center justify-between gap-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="jobtalk" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              JobTalk
              {leadCategoryTab === 'jobtalk' && <Badge variant="secondary">{total}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="franchise" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Franchise
              {leadCategoryTab === 'franchise' && <Badge variant="secondary">{total}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              All
              {leadCategoryTab === 'all' && <Badge variant="secondary">{total}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Lead Type Toggle Buttons - Right Aligned */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {LEAD_TYPES.map(type => (
              <Button
                key={type.value}
                variant={selectedLeadTypes.includes(type.value) ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (selectedLeadTypes.includes(type.value)) {
                    setSelectedLeadTypes(selectedLeadTypes.filter(t => t !== type.value));
                  } else {
                    setSelectedLeadTypes([...selectedLeadTypes, type.value]);
                  }
                }}
                className={`text-xs h-8 px-2 ${selectedLeadTypes.includes(type.value) ? type.color.replace('-100', '-600').replace('-700', '-50') : ''}`}
                data-testid={`toggle-${type.value}`}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        <TabsContent value={leadCategoryTab} className="space-y-4 mt-4">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{total}</div>
          <div className="text-sm text-slate-500">Total Leads</div>
        </CardContent></Card>
        {STATUSES.slice(0, 4).map(s => (
          <Card key={s.value}><CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">{stats[s.value] || 0}</div>
            <div className="text-sm text-slate-500">{s.label}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, company, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-leads"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset filters (use cached data)"
          className="shrink-0"
          data-testid="reset-leads-btn"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh data from database"
          className="shrink-0"
          data-testid="refresh-leads-btn"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <div className="flex items-center gap-2">
          <Checkbox id="hideDNC" checked={hideDNC} onCheckedChange={setHideDNC} />
          <Label htmlFor="hideDNC" className="text-sm cursor-pointer">Hide DNC</Label>
        </div>
        
        {/* Lead Types Multi-Select Filter */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLeadTypeDropdownOpen(!leadTypeDropdownOpen)}
            className="gap-2"
            data-testid="lead-type-filter"
          >
            Lead Types
            {selectedLeadTypes.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedLeadTypes.length}</Badge>
            )}
          </Button>
          {leadTypeDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-50 p-2">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <span className="text-xs font-medium text-slate-500">Filter by Type</span>
                <div className="flex items-center gap-2">
                  {selectedLeadTypes.length > 0 && (
                    <button
                      onClick={() => setSelectedLeadTypes([])}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setLeadTypeDropdownOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="close-lead-type-dropdown"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {LEAD_TYPES.map(type => (
                <label
                  key={type.value}
                  className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedLeadTypes.includes(type.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedLeadTypes([...selectedLeadTypes, type.value]);
                      } else {
                        setSelectedLeadTypes(selectedLeadTypes.filter(t => t !== type.value));
                      }
                    }}
                  />
                  <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
                </label>
              ))}
            </div>
          )}
        </div>
        
        {/* Lists Multi-Select Filter */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setListsDropdownOpen(!listsDropdownOpen)}
            className="gap-2"
            data-testid="lists-filter"
          >
            Lists
            {selectedLists.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{selectedLists.length}</Badge>
            )}
          </Button>
          {listsDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-50 p-2 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-2 pb-2 border-b">
                <span className="text-xs font-medium text-slate-500">Filter by Lists</span>
                <div className="flex items-center gap-2">
                  {selectedLists.length > 0 && (
                    <button
                      onClick={() => setSelectedLists([])}
                      className="text-xs text-indigo-600 hover:underline"
                      title="Clear list filters"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setListsDropdownOpen(false)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="close-lists-dropdown"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* ALL option at the top */}
              <label
                className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer mb-1 border-b"
              >
                <Checkbox
                  checked={selectedLists.length === 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedLists([]);
                    }
                  }}
                />
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-3 h-3 rounded-full flex-shrink-0 bg-slate-400" />
                  <span className="text-sm font-semibold text-slate-900">ALL</span>
                </div>
              </label>
              
              {lists.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No lists available</p>
              ) : (
                <>
                  {lists.map(list => (
                    <label
                      key={list.id}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedLists.includes(list.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLists([...selectedLists, list.id]);
                          } else {
                            setSelectedLists(selectedLists.filter(id => id !== list.id));
                          }
                        }}
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: list.color }}
                        />
                        <span className="text-sm text-slate-700">{list.name}</span>
                      </div>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        
        {selectedIds.size > 0 && (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setBulkUpdateDialogOpen(true)}
              data-testid="bulk-update-btn"
            >
              <Edit className="h-4 w-4 mr-2" />Bulk Update
            </Button>
            <Button 
              size="sm" 
              onClick={openAICallDialog}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="bulk-ai-call-btn"
            >
              <Bot className="h-4 w-4 mr-2" />AI Call {selectedIds.size} Selected
            </Button>
            <Button 
              size="sm" 
              onClick={openSequenceDialog}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-sequence-btn"
            >
              <Zap className="h-4 w-4 mr-2" />Enroll in Sequence
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setBulkDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />Delete {selectedIds.size} Selected
            </Button>
          </div>
        )}
      </div>

      {/* Select All Pages Banner */}
      {selectedIds.size > 0 && selectedIds.size >= 25 && selectedIds.size < total && !selectAllPages && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                All {selectedIds.size} leads on this page are selected.
              </p>
              <p className="text-xs text-blue-700">
                Select all {total} leads across all pages?
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectAllPages(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            Select All {total} Leads
          </Button>
        </div>
      )}

      {/* Select All Pages Active Banner */}
      {selectAllPages && (
        <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-indigo-600" />
            <p className="text-sm font-semibold text-indigo-900">
              All {total} leads across all pages are selected.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectAllPages(false);
              setSelectedIds(new Set());
            }}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Data Grid */}
      {prefsLoaded && (
        <AdvancedDataGrid
          data={filteredLeads}
          columns={columns}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          savedPreferences={columnPrefs}
          onColumnChange={handleColumnChange}
          emptyMessage="No leads found"
          showFilters={showFilters}
          onHoverOpen={(row, columnKey, open) => {
            // Fetch activities when hover card opens
            if (open && columnKey === 'name' && !leadActivitiesCache[row.id]) {
              fetchLeadActivities(row.id);
            }
          }}
          renderRowHoverContent={(row, columnKey) => {
            if (columnKey === 'name') {
              const activities = leadActivitiesCache[row.id] || null;

              return (
                <div className="p-4 w-80">
                  <h4 className="font-semibold text-sm mb-3 text-slate-900">
                    Recent Activity - {row.first_name} {row.last_name}
                  </h4>
                  {!activities ? (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Loading activities...</span>
                    </div>
                  ) : activities.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No recent activity</p>
                  ) : (
                    <div className="space-y-2">
                      {activities.map((activity, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs border-l-2 border-blue-200 pl-2 py-1">
                          <div className="flex-shrink-0 mt-0.5">
                            {activity.type === 'email' && <Mail className="h-3 w-3 text-blue-600" />}
                            {activity.type === 'sms' && <MessageSquare className="h-3 w-3 text-green-600" />}
                            {activity.type === 'call' && <Phone className="h-3 w-3 text-purple-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-700 truncate">
                              {activity.type === 'email' && `Email: ${activity.subject || 'No subject'}`}
                              {activity.type === 'sms' && `SMS: ${(activity.message || 'Message sent').substring(0, 30)}...`}
                              {activity.type === 'call' && `Call: ${activity.outcome || 'Call logged'}`}
                            </p>
                            <p className="text-slate-500">
                              {activity.created_at ? format(parseISO(activity.created_at), 'MMM d, h:mm a') : 'No date'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          }}
        />
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Prefix</Label>
              <Select value={formData.prefix || 'none'} onValueChange={(v) => setFormData({...formData, prefix: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {PREFIXES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>First Name *</Label>
              <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} data-testid="first-name-input" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
            </div>
            <div className="relative company-autocomplete">
              <Label>Company</Label>
              <div className="relative">
                <Input
                  placeholder="Type to search companies..."
                  value={companySearchQuery}
                  onChange={(e) => {
                    const query = e.target.value;
                    setCompanySearchQuery(query);
                    setShowCompanySuggestions(true);
                    // Clear selection if user is typing
                    if (query !== (companies.find(c => c.id === formData.company_id)?.name || '')) {
                      setFormData({...formData, company_id: '', location_id: ''});
                      setSelectedCompanyLocations([]);
                    }
                  }}
                  onFocus={() => setShowCompanySuggestions(true)}
                  className="bg-white text-slate-900"
                  style={{ color: '#000000' }}
                  autoComplete="off"
                />
                
                {showCompanySuggestions && companySearchQuery && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {companies
                      .filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase()))
                      .slice(0, 10)
                      .map((company) => (
                        <div
                          key={company.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm text-slate-900"
                          onClick={() => {
                            setFormData({...formData, company_id: company.id, location_id: ''});
                            setCompanySearchQuery(company.name);
                            setShowCompanySuggestions(false);
                            handleCompanyChange(company.id);
                          }}
                        >
                          {company.name}
                        </div>
                      ))}
                    {companies.filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-400 italic">No companies found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {selectedCompanyLocations.length > 0 && (
              <div>
                <Label>Location</Label>
                <Select value={formData.location_id || 'none'} onValueChange={(v) => setFormData({...formData, location_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {selectedCompanyLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Work Email</Label>
              <Input type="email" value={formData.work_email} onChange={(e) => setFormData({...formData, work_email: e.target.value})} />
            </div>
            <div>
              <Label>Mobile Phone</Label>
              <Input type="tel" value={formData.mobile_phone} onChange={(e) => setFormData({...formData, mobile_phone: e.target.value})} />
            </div>
            <div>
              <Label>Work Phone</Label>
              <Input type="tel" value={formData.work_direct_phone} onChange={(e) => setFormData({...formData, work_direct_phone: e.target.value})} />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})} placeholder="e.g., LinkedIn, Referral" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
            </div>
            <div className="col-span-2 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.dnc} onCheckedChange={(c) => setFormData({...formData, dnc: c})} />
                <Label>Do Not Call</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.dne} onCheckedChange={(c) => setFormData({...formData, dne: c})} />
                <Label>Do Not Email</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLead?.first_name} {selectedLead?.last_name}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Leads?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Wizard */}
      <CSVImportWizard
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        onImportComplete={refreshLeads}
      />

      {/* Bulk AI Call Dialog */}
      <Dialog open={aiCallDialogOpen} onOpenChange={setAiCallDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Initiate AI Calls
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              You&apos;re about to initiate AI calls to <strong>{selectedIds.size}</strong> selected leads.
            </p>
            
            {campaigns.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Campaign *</Label>
                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an active campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>No active campaigns</strong>
                <p className="mt-1">Create and activate a campaign in AI Calls before making bulk calls.</p>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> Only leads with phone numbers will be called. Leads marked as DNC (Do Not Call) will be skipped.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiCallDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAICall} 
              disabled={!selectedCampaignId || aiCallLoading || campaigns.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiCallLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Initiating...
                </>
              ) : (
                <>
                  <Phone className="h-4 w-4 mr-2" />
                  Start AI Calls
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialogOpen} onOpenChange={setBulkUpdateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Bulk Update {selectAllPages ? `All ${total}` : selectedIds.size} Leads
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {selectAllPages 
                ? `Update all ${total} leads across all pages. Changes will be applied to all leads matching current filters.`
                : `Update ${selectedIds.size} selected leads. Changes will be applied immediately.`
              }
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field to Update *</Label>
              <Select value={bulkUpdateField} onValueChange={(value) => {
                setBulkUpdateField(value);
                setBulkUpdateValue('');
                setBulkUpdateValues([]);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="lead_types">Lead Types (Replace All)</SelectItem>
                  <SelectItem value="add_to_lists">Add to Lists</SelectItem>
                  <SelectItem value="owner_id">Owner</SelectItem>
                  <SelectItem value="source">Source</SelectItem>
                  <SelectItem value="title">Title/Position</SelectItem>
                  <SelectItem value="description">Notes/Description</SelectItem>
                  <SelectItem value="dnc">Do Not Call</SelectItem>
                  <SelectItem value="dne">Do Not Email</SelectItem>
                  <SelectItem value="envelope_mailed">Envelope Mailed</SelectItem>
                  <SelectItem value="bad_number">Bad Number</SelectItem>
                  <SelectItem value="gender">Gender</SelectItem>
                  <SelectItem value="prefix">Prefix (Mr/Ms/Dr)</SelectItem>
                  <SelectItem value="timezone">Timezone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Field */}
            {bulkUpdateField === 'status' && (
              <div className="space-y-2">
                <Label>Select Status *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        <Badge className={status.color}>{status.label}</Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Lead Types Field */}
            {bulkUpdateField === 'lead_types' && (
              <div className="space-y-2">
                <Label>Select Lead Types *</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                  {LEAD_TYPES.map(type => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={bulkUpdateValues.includes(type.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setBulkUpdateValues([...bulkUpdateValues, type.value]);
                          } else {
                            setBulkUpdateValues(bulkUpdateValues.filter(v => v !== type.value));
                          }
                        }}
                      />
                      <Badge className={`${type.color} text-xs`}>{type.label}</Badge>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-amber-600">This will REPLACE existing lead types</p>
              </div>
            )}

            {/* Add to Lists Field */}
            {bulkUpdateField === 'add_to_lists' && (
              <div className="space-y-2">
                <Label>Select Lists *</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                  {lists.map(list => (
                    <label key={list.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={bulkUpdateValues.includes(list.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setBulkUpdateValues([...bulkUpdateValues, list.id]);
                          } else {
                            setBulkUpdateValues(bulkUpdateValues.filter(v => v !== list.id));
                          }
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: list.color }} />
                        <span className="text-sm">{list.name}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-blue-600">Leads will be added to selected lists</p>
              </div>
            )}

            {/* Owner Field */}
            {bulkUpdateField === 'owner_id' && (
              <div className="space-y-2">
                <Label>Select Owner *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Boolean Fields - DNC, DNE, Envelope Mailed, Bad Number */}
            {['dnc', 'dne', 'envelope_mailed', 'bad_number'].includes(bulkUpdateField) && (
              <div className="space-y-2">
                <Label>Set {bulkUpdateField.replace('_', ' ').toUpperCase()} to *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose value..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes / True</SelectItem>
                    <SelectItem value="false">No / False</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Gender Field */}
            {bulkUpdateField === 'gender' && (
              <div className="space-y-2">
                <Label>Select Gender *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose gender..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Prefix Field */}
            {bulkUpdateField === 'prefix' && (
              <div className="space-y-2">
                <Label>Select Prefix *</Label>
                <Select value={bulkUpdateValue} onValueChange={setBulkUpdateValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose prefix..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFIXES.map(prefix => (
                      <SelectItem key={prefix} value={prefix}>
                        {prefix}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Text Fields - Source, Title, Description, Timezone */}
            {['source', 'title', 'description', 'timezone'].includes(bulkUpdateField) && (
              <div className="space-y-2">
                <Label>{bulkUpdateField.charAt(0).toUpperCase() + bulkUpdateField.slice(1)} *</Label>
                {bulkUpdateField === 'description' ? (
                  <Textarea
                    placeholder={`Enter ${bulkUpdateField}...`}
                    value={bulkUpdateValue}
                    onChange={(e) => setBulkUpdateValue(e.target.value)}
                    rows={4}
                  />
                ) : (
                  <Input
                    placeholder={`Enter ${bulkUpdateField}...`}
                    value={bulkUpdateValue}
                    onChange={(e) => setBulkUpdateValue(e.target.value)}
                  />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdate} 
              disabled={bulkUpdating || (!bulkUpdateValue && bulkUpdateValues.length === 0)}
            >
              {bulkUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                `Update ${selectAllPages ? total : selectedIds.size} Leads`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Sequence Enrollment Dialog */}
      <Dialog open={sequenceDialogOpen} onOpenChange={setSequenceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Enroll in Sequence
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Enroll <strong>{selectedIds.size}</strong> selected leads in a multi-channel sequence.
            </p>
            
            {sequences.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Sequence *</Label>
                <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an active sequence" />
                  </SelectTrigger>
                  <SelectContent>
                    {sequences.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span>{s.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {(s.steps || []).length} steps
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedSequenceId && sequences.find(s => s.id === selectedSequenceId) && (
                  <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                    <p><strong>Sequence:</strong> {sequences.find(s => s.id === selectedSequenceId)?.name}</p>
                    <p className="mt-1">
                      Steps: {(sequences.find(s => s.id === selectedSequenceId)?.steps || [])
                        .map(step => step.name)
                        .join(' → ')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>No active sequences</strong>
                <p className="mt-1">Create and activate a sequence in the Sequences page before enrolling leads.</p>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> Leads already enrolled in this sequence will be skipped. Each lead can only be enrolled once per sequence.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSequenceDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleEnrollInSequence} 
              disabled={!selectedSequenceId || sequenceEnrollLoading || sequences.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sequenceEnrollLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enrolling...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Enroll Leads
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
